import getExclusionReason from '@app.raytd.com/common/utils/tests/getExclusionReason';
import { toast } from 'sonner';
import { Assessment } from '../assessment/assessment.slice';
import { AssetEntity } from '../asset';
import { TestCase, TestItem, TestSuite } from '../tests/entity';
import type { ReferenceImage, ReferenceImageDisposition } from '../tests/api/referenceImages';
import { TestSpecificationsEntity } from '../tests/test-specifications.slice';
import { UsersEntity } from '../users';
import { transformClassificationTree } from './elementTransformer';
import {
  ActivityItem,
  AddCustomFieldOptionRequest,
  AddCustomFieldOptionResponse,
  ClassificationItem,
  DashboardRecentResponse,
  FastFillUpdateRequest,
  GetTestItemRequest,
  PublishedReportResponse,
  PublishReportPayload,
  PublishReportResponse,
  ReportBlock,
  ReportEntity,
  ReportPayload,
  ReportResponse,
  ReportRevision,
  ReportSection,
  ReportTestItem,
  ReportUpdatePayload,
  ToggleSwitchRequest,
  ToggleSwitchResponse,
  TestItemComment,
} from './types';
import updateItemInReportCache from './updateItemInReportCache';
import {
  NotificationPagination,
  AppNotification,
  ReportExport,
} from '../notifications/types';
import { rAPI } from './rAPI';

export const notificationsAPI = rAPI.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    getNotifications: builder.query<NotificationPagination, { page?: number } | void>({
      query: (params) => {
        const page = params && typeof params === 'object' && 'page' in params ? params.page : undefined;
        const queryParams = page ? `?page=${page}` : '';
        return `notifications${queryParams}`;
      },
      transformResponse: (response: unknown): NotificationPagination => {
        // Type guard helper
        const hasProperty = (obj: unknown, prop: string): boolean => {
          return typeof obj === 'object' && obj !== null && prop in obj;
        };
        
        const getProperty = <T>(obj: unknown, prop: string): T | undefined => {
          if (hasProperty(obj, prop)) {
            return (obj as Record<string, T>)[prop];
          }
          return undefined;
        };
        
        // Handle different possible response structures from Laravel
        // Case 1: Standard Laravel paginated response with nested data
        // { data: { data: [...], current_page: 1, ... }, unread_count: 5 }
        const nestedData = getProperty<{ data?: AppNotification[] }>(response, 'data');
        if (nestedData && Array.isArray(nestedData.data)) {
          return response as NotificationPagination;
        }
        
        // Case 2: Flat structure where data is directly an array
        // { data: [...], unread_count: 5 }
        const flatData = getProperty<AppNotification[]>(response, 'data');
        const unreadCount = getProperty<number>(response, 'unread_count');
        if (Array.isArray(flatData) && typeof unreadCount === 'number') {
          return {
            data: {
              data: flatData,
              current_page: 1,
              per_page: flatData.length,
              total: flatData.length,
              last_page: 1,
              from: flatData.length > 0 ? 1 : null,
              to: flatData.length > 0 ? flatData.length : null,
              links: {
                first: null,
                last: null,
                prev: null,
                next: null,
              },
            },
            unread_count: unreadCount,
          };
        }
        
        // Case 3: Response is the pagination object directly
        // { data: [...], current_page: 1, ..., unread_count: 5 }
        if (Array.isArray(flatData) && typeof getProperty<number>(response, 'current_page') === 'number') {
          const currentPage = getProperty<number>(response, 'current_page') ?? 1;
          const perPage = getProperty<number>(response, 'per_page') ?? flatData.length;
          const total = getProperty<number>(response, 'total') ?? flatData.length;
          const lastPage = getProperty<number>(response, 'last_page') ?? 1;
          const from = getProperty<number | null>(response, 'from') ?? (flatData.length > 0 ? 1 : null);
          const to = getProperty<number | null>(response, 'to') ?? (flatData.length > 0 ? flatData.length : null);
          const links = getProperty<NotificationPagination['data']['links']>(response, 'links') ?? {
            first: null,
            last: null,
            prev: null,
            next: null,
          };
          const meta = getProperty<{ path: string }>(response, 'meta');
          
          return {
            data: {
              data: flatData,
              current_page: currentPage,
              per_page: perPage,
              total: total,
              last_page: lastPage,
              from: from,
              to: to,
              links: links,
              ...(meta && { meta }),
            },
            unread_count: unreadCount ?? 0,
          };
        }
        
        // Fallback: Return empty structure
        console.warn('Unexpected notifications API response structure:', response);
        return {
          data: {
            data: [],
            current_page: 1,
            per_page: 0,
            total: 0,
            last_page: 1,
            from: null,
            to: null,
            links: {
              first: null,
              last: null,
              prev: null,
              next: null,
            },
          },
          unread_count: 0,
        };
      },
      providesTags: (result) => {
        const tags: Array<{ type: 'Notifications'; id: string }> = [
          { type: 'Notifications', id: 'LIST' },
        ];
        if (result?.data?.data) {
          result.data.data.forEach((notification: AppNotification) => {
            tags.push({ type: 'Notifications', id: notification.id });
          });
        }
        return tags;
      },
    }),

    markNotificationAsRead: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `notifications/${id}/read`,
        method: 'POST',
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        // Optimistic update: mark notification as read
        const patchResult = dispatch(
          notificationsAPI.util.updateQueryData('getNotifications', undefined, (draft) => {
            const notification = draft.data.data.find((n: AppNotification) => n.id === id);
            if (notification && notification.read_at === null) {
              notification.read_at = new Date().toISOString();
              draft.unread_count = Math.max(0, draft.unread_count - 1);
            }
          })
        );

        try {
          await queryFulfilled;
        } catch (error) {
          // Revert optimistic update on error
          if (patchResult) {
            patchResult.undo();
          }
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Notifications', id },
        { type: 'Notifications', id: 'LIST' },
      ],
    }),

    markAllNotificationsAsRead: builder.mutation<void, void>({
      query: () => ({
        url: 'notifications/read-all',
        method: 'POST',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        // Optimistic update: mark all notifications as read
        const patchResult = dispatch(
          notificationsAPI.util.updateQueryData('getNotifications', undefined, (draft) => {
            const now = new Date().toISOString();
            draft.data.data.forEach((notification: AppNotification) => {
              if (notification.read_at === null) {
                notification.read_at = now;
              }
            });
            draft.unread_count = 0;
          })
        );

        try {
          await queryFulfilled;
        } catch (error) {
          // Revert optimistic update on error
          if (patchResult) {
            patchResult.undo();
          }
        }
      },
      invalidatesTags: [{ type: 'Notifications', id: 'LIST' }],
    }),

    getActiveExports: builder.query<ReportExport[], void>({
      query: () => 'report-exports/active',
      // The endpoint always answers `{ data: [...] }` (already filtered to pending/processing);
      // unwrap it so consumers get the array the type promises (web#200).
      transformResponse: (response: { data: ReportExport[] }) => response.data,
      providesTags: ['ActiveExports'],
    }),

    getDashboardRecent: builder.query<DashboardRecentResponse, void>({
      query: () => 'dashboard/my-recent',
      providesTags: ['Dashboard'],
    }),

    getDashboardActivity: builder.query<ActivityItem[], void>({
      query: () => 'dashboard/activity',
      transformResponse: (response: { data?: ActivityItem[] } | ActivityItem[]) => {
        // Handle both wrapped { data: [...] } and direct array responses
        if (Array.isArray(response)) {
          return response
        }
        return response.data || []
      },
      providesTags: ['Activity'],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  useGetActiveExportsQuery,
  useGetDashboardRecentQuery,
  useGetDashboardActivityQuery,
} = notificationsAPI;
